import { Tag } from "@chakra-ui/react";
import { ProfileHeaderStat } from "components/profile/profile-header";
import colorSchemes from "data/color-schemes";
import type { CompanyTagName, CompanyTags } from "data/company-tags";
import type { CompanyClassification } from "types/api/company";

const getCompanyStatus = (tags: CompanyTags): CompanyTagName | null => {
  const companyTagList = [];

  if (tags) {
    const acquired = tags.find(({ tag }) => tag === "Acquired");
    if (acquired) {
      companyTagList.push(acquired.tag);
    }
  
    const dissolved = tags.find(({ tag }) => tag === "Dissolved");
    if (dissolved) {
      companyTagList.push(dissolved.tag);
    }
  
    const formerlyPublic = tags.find(({ tag }) => tag === "Formerly Public");
    if (formerlyPublic) {
      companyTagList.push(formerlyPublic.tag);
    }
  
    const integrated = tags.find(({ tag }) => tag === "Integrated");
    if (integrated) {
      companyTagList.push(integrated.tag);
    }
  
    const jointVenture = tags.find(({ tag }) => tag === "Joint Venture");
    if (jointVenture) {
      companyTagList.push(jointVenture.tag);
    }
  
    const multinational = tags.find(({ tag }) => tag === "Multinational");
    if (multinational) {
      companyTagList.push(multinational.tag);
    }
  
    const peBacked = tags.find(({ tag }) => tag === "PE Backed");
    if (peBacked) {
      companyTagList.push(peBacked.tag);
    }
  
    const platform = tags.find(({ tag }) => tag === "Platform");
    if (platform) {
      companyTagList.push(platform.tag);
    }
  
    const subsidiary = tags.find(({ tag }) => tag === "Subsidiary");
    if (subsidiary) {
      companyTagList.push(subsidiary.tag);
    }
  
    const vcBacked = tags.find(({ tag }) => tag === "VC Backed");
    if (vcBacked) {
      companyTagList.push(vcBacked.tag);
    }
  
    const formerlyVcBacked = tags.find(({ tag }) => tag === "Formerly VC-Backed");
    if (formerlyVcBacked) {
      companyTagList.push(formerlyVcBacked.tag);
    }
  
    const formerlyPeBacked = tags.find(({ tag }) => tag === "Formerly PE-Backed");
    if (formerlyPeBacked) {
      companyTagList.push(formerlyPeBacked.tag);
    }
  
    const ipoFiled = tags.find(({ tag }) => tag === "IPO Filed");
    if (ipoFiled) {
      companyTagList.push(ipoFiled.tag);
    }
  
    const bankruptcyFiled = tags.find(({ tag }) => tag === "Bankruptcy Filed");
    if (bankruptcyFiled) {
      companyTagList.push(bankruptcyFiled.tag);
    }
  
    const bankruptcyExited = tags.find(({ tag }) => tag === "Bankruptcy Exited");
    if (bankruptcyExited) {
      companyTagList.push(bankruptcyExited.tag);
    }

    const companyTagVals : any = companyTagList.join(", ");
    return companyTagVals;
  }

  return null;
};

interface CompanyStatusProps {
  tags: CompanyTags;
}

export const CompanyStatusStat = ({ tags }: CompanyStatusProps) => {
  const status = getCompanyStatus(tags);

  if (!status) {
    return null;
  }

  return <ProfileHeaderStat label="Status">{status}</ProfileHeaderStat>;
};

const getClassificationColorScheme = (
  classification: CompanyClassification
): string => {
  const cleanClassification = classification
    .toLowerCase()
    .replace(/[^a-z]+/g, "");

  switch (cleanClassification) {
    case "public":
      return colorSchemes.public;

    case "private":
      return colorSchemes.private;

    case "nonprofit":
      return colorSchemes.nonProfit;

    case "stateowned":
      return colorSchemes.stateOwned;

    default:
      return "none";
  }
};

interface CompanyClassificationProps {
  classification: CompanyClassification;
  stockTickers: string[];
}

export const CompanyClassificationStat = ({
  classification,
  stockTickers = [],
}: CompanyClassificationProps) => {
  const colorScheme = getClassificationColorScheme(classification);

  let value = classification;

  if (classification === "Public" && stockTickers.length) {
    value += ` as ${stockTickers.join(", ")}`;
  }

  return (
    <ProfileHeaderStat label="Classification">
      {value ? <Tag colorScheme={colorScheme}>{value}</Tag> : "--"}
    </ProfileHeaderStat>
  );
};
